import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import { Paper } from '@gto/ui/paper'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import { DeltaIndicator } from '@/components/delta-indicator'
import { Sparkline } from '@/components/sparkline'

type DashboardKpiTileProps = {
  label: ReactNode
  value: ReactNode
  icon?: ReactNode
  tooltip?: ReactNode
  /** Percent change vs the prior window as a whole-number percent (12 == +12%). */
  deltaPct?: number | null
  /** Series for the inline sparkline, at the page granularity. Omit for metrics without a series. */
  sparklineData?: number[]
  /**
   * Render the value in the muted secondary color — the same zero treatment
   * the admin tables use, so an all-zeros tile reads as "nothing yet", not
   * as a headline number.
   */
  muted?: boolean
  loading?: boolean
  className?: string
  /**
   * Makes the whole tile a real button (drill-down into the metric's detail
   * view). Adds the pointer + hover/focus affordances.
   */
  onClick?: () => void
}

export const DashboardKpiTile = ({
  label,
  value,
  icon,
  tooltip,
  deltaPct,
  sparklineData,
  muted = false,
  loading = false,
  className,
  onClick,
}: DashboardKpiTileProps) => {
  const getDirection = () => {
    if (deltaPct == null) {
      return 'neutral'
    }

    return deltaPct >= 0 ? 'up' : 'down'
  }
  const direction = getDirection()

  // min-w-[140px] is a deliberate one-off responsive floor — no token in
  // libs/ui/src/theme/design-tokens/spacing.ts (the closest, 128px / 160px,
  // either breaks the desktop grid or wraps too aggressively); kept until
  // a `card-min` token lands.
  const tileClassName = cn(
    'flex min-w-[140px] flex-[1_1_0px] flex-col gap-3 rounded-md bg-gradient-primary-vertical p-4 md:flex-[1_0_calc(50%-4px)] sm:flex-[1_0_100%]',
    onClick != null &&
      'cursor-pointer border border-transparent text-left transition-[border-color] duration-shorter focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-main hover:border-divider',
    className,
  )

  return (
    <Paper
      as={onClick == null ? 'div' : 'button'}
      className={tileClassName}
      elevation="2"
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        {icon != null && (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-primary-states-focus [&_svg]:size-5 [&_svg]:text-primary-main">
            {icon}
          </div>
        )}
        <Typography
          // tracking-[0.06em] = ~1px letterspacing for 11-12px text; no
          // letterSpacing token covers em-based values (the design tokens are
          // all px). The KPI label is the canonical uppercase-eyebrow on this
          // page; preserve the spacing until a token lands.
          className="text-xs font-medium uppercase tracking-[0.06em]"
          color="text.secondary"
          variant="body2"
        >
          {label}
        </Typography>
        {tooltip != null && (
          <HelpIconTooltip
            // Paper renders as='button' when a caller passes onClick — keep
            // the trigger non-interactive so that stays valid.
            embedded
            content={tooltip}
            size="xs"
          />
        )}
      </div>

      {loading ? (
        <Skeleton
          height={36}
          variant="rectangular"
          width={100}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Typography
              className="font-heading font-semibold leading-100"
              color={muted ? 'text.secondary' : 'text.primary'}
              variant="h4"
            >
              {value}
            </Typography>
            {deltaPct != null && (
              <span className="inline-flex items-center rounded-full bg-paper-elevation4 px-2 py-0.5">
                <DeltaIndicator value={deltaPct} />
              </span>
            )}
          </div>
          {sparklineData != null && (
            <Sparkline
              className="mt-auto"
              data={sparklineData}
              direction={direction}
            />
          )}
        </>
      )}
    </Paper>
  )
}
